import { Injectable } from '@angular/core';
import { MenuItem } from '@/core/models';
import {
	CartEmptyRule,
	CartQuantityUpdateRule,
	CartTotalQuantityRule,
} from './rules';
import { CartCalculationRule, CartDomainContext } from './cart.domain';
import { CartFactoryService } from '.';
import { IntegerPrecisionService } from '@/core/services';

@Injectable({
	providedIn: 'root',
})
export class CartProcessorService {
	constructor(private readonly cartFactory: CartFactoryService, private readonly integerPrecisionService: IntegerPrecisionService) {}

	/**
	 * Determina si el carrito actual está vacío
	 */
	public isCartEmpty(items: MenuItem[] | null | undefined): boolean {
		return CartEmptyRule.isSatisfiedBy(items);
	}

	/**
	 * Procesa el cambio de cantidad de un ítem del carrito, aplicando la regla de precisión.
	 * @param item MenuItem
	 * @param newQuantity number | string
	 * @returns MenuItem
	 */
	public processQuantityChange<T extends { cantidad?: bigint }>(item: T, newQuantity?: number | string): T {
		return CartQuantityUpdateRule.updateQuantity(item, newQuantity);
	}

	/**
	 * Calcula el precio y precio con cash por cada articulo.
	 * @param menuItems MenuItem[]
	 * @returns MenuItem[]
	 */
	public calculateCartsWithSubtotalPrice(menuItems: MenuItem[]): CartDomainContext {
		let context = {
			menuItems,
		};

		// Obtenemos las reglas en orden
		const rules = this.cartFactory.getItemPriceRules();

		// Recorremos todas las reglas aplicandolas al contexto, pasandole el resultado de la regla anterior como entrada
		return rules.reduce((ctx: CartDomainContext, rule: CartCalculationRule) => rule.calculate(ctx), context);
	}

	/**
	 * Sincroniza el stock de los items de la nube con los items del carrito.
	 * @param menuItems MenuItem[]
	 * @returns MenuItem[]
	 */
	public syncMenuItemStock(menuItems: MenuItem[], cloudMenuItems: MenuItem[]): MenuItem[] {
		let context: CartDomainContext = {
			menuItems,
			cloudMenuItems,
		};

		// Obtenemos las reglas en orden
		const rules = this.cartFactory.getCartStockRules();

		// Recorremos todas las reglas aplicandolas al contexto, pasandole el resultado de la regla anterior como entrada
		return rules.reduce((ctx: CartDomainContext, rule: CartCalculationRule) => rule.calculate(ctx), context).menuItems;
	}

	/**
	 * Sincroniza los precios de los items de la nube con los items del carrito.
	 * @param menuItems MenuItem[]
	 * @returns MenuItem[]
	 */
	public syncMenuItem(menuItems: MenuItem[], cloudMenuItems: MenuItem[]): CartDomainContext {
		let context: CartDomainContext = {
			menuItems,
			cloudMenuItems,
			precisionService: this.integerPrecisionService,
			cartItemChanged: false,
		};

		const rules = this.cartFactory.getCartItemSyncRules();

		return rules.reduce((ctx: CartDomainContext, rule: CartCalculationRule) => rule.calculate(ctx), context);
	}

	/**
	 * Total de menu items agregados al carrito.
	 */
	public getTotalQuantity(items: MenuItem[]): number {
		return CartTotalQuantityRule.calculate(items);
	}
}
